import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  BarChart3,
  UserCheck,
  MessageSquare,
  CheckCircle,
  Clock,
  TrendingUp,
  Loader2,
  RefreshCw,
  Calendar,
  Award,
  Target,
  Star,
  ArrowUp,
  ArrowDown,
  Trophy,
  Sparkles
} from 'lucide-react';
import { getAttendantMetrics, getSupervisorReports } from '../lib/api';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
  RadialBarChart,
  RadialBar,
  LineChart,
  Line
} from 'recharts';

// ─── Chart Tooltip ───
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-lg">
        <p className="text-sm font-semibold text-slate-800 mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: <span className="font-medium">{entry.value}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// ─── Metric Card ───
const MetricCard = ({ title, value, subtitle, icon: Icon, color, trend, trendValue, description }) => (
  <Card className="bg-white border border-slate-200 hover:shadow-md transition-all duration-300 group">
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div className="space-y-1.5">
          <p className="text-sm font-medium text-slate-500">{title}</p>
          <p className="text-3xl font-bold text-slate-900">{value}</p>
          {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
          {description && <p className="text-xs text-slate-400 italic">{description}</p>}
          {trend && (
            <div className={cn(
              "flex items-center gap-1 text-xs font-medium mt-0.5",
              trend === 'up' ? 'text-emerald-600' : 'text-red-500'
            )}>
              {trend === 'up' ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
              <span>{trendValue}</span>
            </div>
          )}
        </div>
        <div className={cn(
          "p-3 rounded-xl transition-transform group-hover:scale-110",
          color === 'blue' && "bg-blue-50",
          color === 'green' && "bg-emerald-50",
          color === 'purple' && "bg-violet-50",
          color === 'orange' && "bg-orange-50",
          color === 'yellow' && "bg-amber-50",
          color === 'pink' && "bg-pink-50"
        )}>
          <Icon className={cn(
            "w-6 h-6",
            color === 'blue' && "text-blue-500",
            color === 'green' && "text-emerald-500",
            color === 'purple' && "text-violet-500",
            color === 'orange' && "text-orange-500",
            color === 'yellow' && "text-amber-500",
            color === 'pink' && "text-pink-500"
          )} />
        </div>
      </div>
    </CardContent>
  </Card>
);

// ─── Rating Stars ───
const RatingStars = ({ rating }) => {
  const stars = [];
  const fullStars = Math.floor(rating);
  const hasHalf = rating % 1 >= 0.5;

  for (let i = 0; i < 5; i++) {
    if (i < fullStars) {
      stars.push(<Star key={i} size={16} className="fill-amber-400 text-amber-400" />);
    } else if (i === fullStars && hasHalf) {
      stars.push(<Star key={i} size={16} className="fill-amber-400/50 text-amber-400" />);
    } else {
      stars.push(<Star key={i} size={16} className="text-slate-200" />);
    }
  }
  return <div className="flex gap-0.5">{stars}</div>;
};

export const SupervisorReports = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState([]);
  const [reports, setReports] = useState(null);
  const [period, setPeriod] = useState('week');

  useEffect(() => {
    loadData();
  }, [period]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [metricsRes, reportsRes] = await Promise.all([
        getAttendantMetrics(),
        getSupervisorReports(period)
      ]);
      setMetrics(metricsRes.data || []);
      setReports(reportsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    return status === 'online' ? (
      <Badge className="bg-emerald-50 text-emerald-600 border border-emerald-200 text-xs">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1.5 inline-block" />
        Online
      </Badge>
    ) : (
      <Badge variant="secondary" className="bg-slate-100 text-slate-500 text-xs">Offline</Badge>
    );
  };

  const getPeriodLabel = () => {
    switch (period) {
      case 'today': return 'Hoje';
      case 'week': return 'Últimos 7 dias';
      case 'month': return 'Últimos 30 dias';
      default: return period;
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
            <Loader2 className="h-7 w-7 animate-spin text-primary" />
          </div>
          <p className="text-slate-500 font-medium">Carregando relatórios...</p>
        </div>
      </div>
    );
  }

  // Prepare chart data
  const attendantPerformance = metrics.map(att => ({
    name: att.name?.split(' ')[0] || 'N/A',
    conversas: att.closed_today || 0,
    mensagens: att.messages_today || 0,
    ativas: att.active_conversations || 0
  }));

  // Satisfaction distribution
  const satisfactionData = reports?.satisfaction?.distribution ?
    Object.entries(reports.satisfaction.distribution).map(([key, value]) => ({
      name: `${key} ⭐`,
      value: value,
      fill: key === '5' ? '#10b981' : key === '4' ? '#84cc16' : key === '3' ? '#f59e0b' : key === '2' ? '#f97316' : '#ef4444'
    })) : [];

  // Daily trend
  const dailyTrend = [
    { day: 'Seg', conversas: 45, mensagens: 234 },
    { day: 'Ter', conversas: 52, mensagens: 287 },
    { day: 'Qua', conversas: 38, mensagens: 198 },
    { day: 'Qui', conversas: 61, mensagens: 345 },
    { day: 'Sex', conversas: 55, mensagens: 298 },
    { day: 'Sáb', conversas: 28, mensagens: 156 },
    { day: 'Dom', conversas: 15, mensagens: 89 }
  ];

  // Resolution rate gauge data
  const resolutionRate = reports?.summary?.resolution_rate || 0;
  const gaugeData = [
    { name: 'Taxa', value: resolutionRate, fill: resolutionRate >= 80 ? '#10b981' : resolutionRate >= 50 ? '#f59e0b' : '#ef4444' }
  ];

  // Compute totals for attendants tab
  const totalOnline = metrics.filter(a => a.status === 'online').length;
  const totalActive = metrics.reduce((s, a) => s + (a.active_conversations || 0), 0);
  const totalClosed = metrics.reduce((s, a) => s + (a.closed_today || 0), 0);
  const totalMsgs = metrics.reduce((s, a) => s + (a.messages_today || 0), 0);

  return (
    <div className="flex-1 overflow-auto bg-slate-50">
      <div className="p-8 space-y-6 max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              Relatórios
            </h1>
            <p className="text-slate-500 mt-0.5 text-sm">
              Análise de desempenho e métricas da equipe
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-[180px] bg-white border-slate-200">
                <Calendar className="h-4 w-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Período" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Hoje</SelectItem>
                <SelectItem value="week">Últimos 7 dias</SelectItem>
                <SelectItem value="month">Últimos 30 dias</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={loadData} className="gap-2 bg-white">
              <RefreshCw className="h-4 w-4" />
              Atualizar
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        {reports && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              title="Total de Conversas"
              value={reports.summary?.total_conversations || 0}
              subtitle={getPeriodLabel()}
              icon={MessageSquare}
              color="blue"
            />
            <MetricCard
              title="Finalizadas"
              value={reports.summary?.closed_conversations || 0}
              subtitle="Resolvidas com sucesso"
              icon={CheckCircle}
              color="green"
            />
            <MetricCard
              title="Taxa de Resolução"
              value={`${reports.summary?.resolution_rate || 0}%`}
              subtitle="Conversas finalizadas"
              icon={Target}
              color="purple"
              description={resolutionRate >= 80 ? 'Excelente desempenho' : resolutionRate >= 50 ? 'Pode melhorar' : 'Atenção necessária'}
            />
            <MetricCard
              title="Total de Mensagens"
              value={reports.summary?.total_messages || 0}
              subtitle="Trocadas no período"
              icon={BarChart3}
              color="orange"
            />
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-5">
          <TabsList className="bg-white border border-slate-200 p-1">
            <TabsTrigger value="overview" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-white">
              <BarChart3 size={15} />
              Visão Geral
            </TabsTrigger>
            <TabsTrigger value="attendants" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-white">
              <UserCheck size={15} />
              Atendentes
            </TabsTrigger>
            <TabsTrigger value="satisfaction" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-white">
              <Star size={15} />
              Satisfação
            </TabsTrigger>
            <TabsTrigger value="ranking" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-white">
              <Award size={15} />
              Ranking
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Daily Trend Chart */}
              <Card className="lg:col-span-2 bg-white border border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                    <TrendingUp size={18} className="text-primary" />
                    Tendência Semanal
                  </CardTitle>
                  <CardDescription className="text-xs text-slate-400">Volume de conversas e mensagens por dia da semana</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={dailyTrend}>
                        <defs>
                          <linearGradient id="colorConv" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#6366f1" stopOpacity={0.15} />
                            <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="colorMsg" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.15} />
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="day" stroke="#94a3b8" fontSize={12} tickLine={false} />
                        <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend
                          verticalAlign="top"
                          height={36}
                          formatter={(value) => <span className="text-xs text-slate-600">{value}</span>}
                        />
                        <Area type="monotone" dataKey="conversas" name="Conversas" stroke="#6366f1" fill="url(#colorConv)" strokeWidth={2.5} />
                        <Area type="monotone" dataKey="mensagens" name="Mensagens" stroke="#10b981" fill="url(#colorMsg)" strokeWidth={2.5} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Resolution Rate Gauge */}
              <Card className="bg-white border border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                    <Target size={18} className="text-primary" />
                    Taxa de Resolução
                  </CardTitle>
                  <CardDescription className="text-xs text-slate-400">Porcentagem de conversas finalizadas</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[260px] flex flex-col items-center justify-center">
                    <ResponsiveContainer width="100%" height="70%">
                      <RadialBarChart
                        cx="50%"
                        cy="50%"
                        innerRadius="60%"
                        outerRadius="90%"
                        data={gaugeData}
                        startAngle={180}
                        endAngle={0}
                      >
                        <RadialBar
                          minAngle={15}
                          background={{ fill: '#f1f5f9' }}
                          clockWise
                          dataKey="value"
                          cornerRadius={10}
                        />
                      </RadialBarChart>
                    </ResponsiveContainer>
                    <div className="text-center -mt-14">
                      <p className="text-4xl font-bold text-slate-900">{resolutionRate}%</p>
                      <p className={cn(
                        "text-sm font-medium mt-1",
                        resolutionRate >= 80 ? "text-emerald-600" : resolutionRate >= 50 ? "text-amber-600" : "text-red-500"
                      )}>
                        {resolutionRate >= 80 ? '✓ Excelente' : resolutionRate >= 50 ? '⚠ Regular' : '✗ Precisa melhorar'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Attendant Performance Bar Chart */}
            {attendantPerformance.length > 0 && (
              <Card className="bg-white border border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                    <UserCheck size={18} className="text-primary" />
                    Desempenho por Atendente
                  </CardTitle>
                  <CardDescription className="text-xs text-slate-400">Comparativo de conversas encerradas e mensagens enviadas por atendente</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={attendantPerformance} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis type="number" stroke="#94a3b8" fontSize={12} tickLine={false} />
                        <YAxis dataKey="name" type="category" stroke="#94a3b8" fontSize={12} width={80} tickLine={false} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend
                          verticalAlign="top"
                          height={36}
                          formatter={(value) => <span className="text-xs text-slate-600">{value}</span>}
                        />
                        <Bar dataKey="conversas" name="Conversas" fill="#6366f1" radius={[0, 4, 4, 0]} />
                        <Bar dataKey="mensagens" name="Mensagens" fill="#10b981" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Attendants Tab */}
          <TabsContent value="attendants" className="space-y-4">
            {/* Attendant summary row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-white border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-emerald-600">{totalOnline}</p>
                <p className="text-xs text-slate-400 mt-0.5">Online agora</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{totalActive}</p>
                <p className="text-xs text-slate-400 mt-0.5">Conversas ativas</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-violet-600">{totalClosed}</p>
                <p className="text-xs text-slate-400 mt-0.5">Finalizadas hoje</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-orange-600">{totalMsgs}</p>
                <p className="text-xs text-slate-400 mt-0.5">Msgs enviadas hoje</p>
              </div>
            </div>

            <Card className="bg-white border border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-slate-800">Métricas dos Atendentes</CardTitle>
                <CardDescription className="text-slate-500 text-xs">Desempenho individual de cada membro da equipe</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-200">
                      <TableHead className="text-slate-500 text-xs font-semibold uppercase tracking-wider">Atendente</TableHead>
                      <TableHead className="text-slate-500 text-xs font-semibold uppercase tracking-wider">Status</TableHead>
                      <TableHead className="text-center text-slate-500 text-xs font-semibold uppercase tracking-wider">Ativas</TableHead>
                      <TableHead className="text-center text-slate-500 text-xs font-semibold uppercase tracking-wider">Finalizadas</TableHead>
                      <TableHead className="text-center text-slate-500 text-xs font-semibold uppercase tracking-wider">Mensagens</TableHead>
                      <TableHead className="text-center text-slate-500 text-xs font-semibold uppercase tracking-wider">Avaliação</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {metrics.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-slate-400 py-12">
                          <UserCheck className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                          Nenhum atendente cadastrado
                        </TableCell>
                      </TableRow>
                    ) : (
                      metrics.map((att, index) => (
                        <TableRow key={att.id} className="hover:bg-slate-50/80 border-slate-100">
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className={cn(
                                "w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm",
                                index === 0 ? "bg-amber-100 text-amber-700" :
                                  index === 1 ? "bg-slate-200 text-slate-600" :
                                    index === 2 ? "bg-orange-100 text-orange-700" :
                                      "bg-primary/10 text-primary"
                              )}>
                                {index < 3 ? ['🥇', '🥈', '🥉'][index] : att.name?.charAt(0)}
                              </div>
                              <div>
                                <p className="font-semibold text-slate-800">{att.name}</p>
                                <p className="text-xs text-slate-400">{att.email}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{getStatusBadge(att.status)}</TableCell>
                          <TableCell className="text-center">
                            <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-blue-50 text-blue-700 font-bold text-sm">
                              {att.active_conversations || 0}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 font-bold text-sm">
                              {att.closed_today || 0}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-violet-50 text-violet-700 font-bold text-sm">
                              {att.messages_today || 0}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            {att.average_rating ? (
                              <div className="flex items-center justify-center gap-2">
                                <RatingStars rating={att.average_rating} />
                                <span className="text-sm font-semibold text-slate-700">{att.average_rating.toFixed(1)}</span>
                              </div>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Satisfaction Tab */}
          <TabsContent value="satisfaction" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Average Rating */}
              <Card className="bg-white border border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                    <Sparkles size={18} className="text-amber-500" />
                    Nota Média
                  </CardTitle>
                  <CardDescription className="text-xs text-slate-400">Avaliação média dos clientes no período</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col items-center justify-center py-8">
                  <div className="text-6xl font-bold text-amber-500">
                    {reports?.satisfaction?.average_rating ? Number(reports.satisfaction.average_rating).toFixed(1) : '-'}
                  </div>
                  <div className="mt-3">
                    <RatingStars rating={reports?.satisfaction?.average_rating || 0} />
                  </div>
                  <p className="text-slate-500 mt-3 text-sm">
                    Baseado em <span className="font-semibold text-slate-700">{reports?.satisfaction?.total_surveys || 0}</span> avaliações
                  </p>
                  {reports?.satisfaction?.average_rating && (
                    <Badge variant="outline" className={cn(
                      "mt-3 text-xs border",
                      reports.satisfaction.average_rating >= 4 ? "bg-emerald-50 text-emerald-600 border-emerald-200" :
                        reports.satisfaction.average_rating >= 3 ? "bg-amber-50 text-amber-600 border-amber-200" :
                          "bg-red-50 text-red-600 border-red-200"
                    )}>
                      {reports.satisfaction.average_rating >= 4 ? '😊 Positivo' :
                        reports.satisfaction.average_rating >= 3 ? '😐 Neutro' : '😞 Negativo'}
                    </Badge>
                  )}
                </CardContent>
              </Card>

              {/* Distribution Pie Chart */}
              <Card className="bg-white border border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                    <BarChart3 size={18} className="text-primary" />
                    Distribuição das Notas
                  </CardTitle>
                  <CardDescription className="text-xs text-slate-400">Proporção de cada nota recebida</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[250px]">
                    {satisfactionData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={satisfactionData}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={80}
                            paddingAngle={5}
                            dataKey="value"
                          >
                            {satisfactionData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Pie>
                          <Tooltip content={<CustomTooltip />} />
                          <Legend
                            formatter={(value) => <span className="text-xs text-slate-600">{value}</span>}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center text-slate-400">
                        <div className="text-center">
                          <Star className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                          <p className="text-sm">Sem dados de satisfação</p>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Satisfaction Details */}
            {reports?.satisfaction?.distribution && (
              <Card className="bg-white border border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold text-slate-800">Detalhamento por Nota</CardTitle>
                  <CardDescription className="text-xs text-slate-400">Quantidade de avaliações por estrela</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[5, 4, 3, 2, 1].map((rating) => {
                      const count = reports.satisfaction.distribution[rating] || 0;
                      const total = reports.satisfaction.total_surveys || 1;
                      const percentage = Math.round((count / total) * 100);

                      return (
                        <div key={rating} className="flex items-center gap-4">
                          <div className="flex items-center gap-1.5 w-24">
                            <span className="font-bold text-slate-700 w-4 text-right">{rating}</span>
                            <Star size={16} className="fill-amber-400 text-amber-400" />
                          </div>
                          <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className={cn(
                                "h-full rounded-full transition-all duration-500",
                                rating === 5 && "bg-emerald-500",
                                rating === 4 && "bg-lime-500",
                                rating === 3 && "bg-amber-500",
                                rating === 2 && "bg-orange-500",
                                rating === 1 && "bg-red-500"
                              )}
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          <div className="w-20 text-right">
                            <span className="text-sm font-semibold text-slate-700">{count}</span>
                            <span className="text-xs text-slate-400 ml-1">({percentage}%)</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Ranking Tab */}
          <TabsContent value="ranking" className="space-y-4">
            <Card className="bg-white border border-slate-200">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-amber-50">
                    <Trophy className="text-amber-500 w-6 h-6" />
                  </div>
                  <div>
                    <CardTitle className="text-slate-800">Ranking de Atendentes</CardTitle>
                    <CardDescription className="text-slate-500 text-xs">Classificação baseada em conversas finalizadas hoje</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {metrics.length === 0 ? (
                    <div className="text-center py-12">
                      <Award className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                      <p className="text-slate-400">Nenhum atendente cadastrado</p>
                    </div>
                  ) : (
                    [...metrics]
                      .sort((a, b) => (b.closed_today || 0) - (a.closed_today || 0))
                      .map((att, index) => (
                        <div
                          key={att.id}
                          className={cn(
                            "flex items-center justify-between p-4 rounded-xl transition-all",
                            index === 0 && "bg-gradient-to-r from-amber-50 to-amber-50/40 border border-amber-200 shadow-sm",
                            index === 1 && "bg-slate-50 border border-slate-200",
                            index === 2 && "bg-orange-50/60 border border-orange-200",
                            index > 2 && "bg-slate-50/60 border border-slate-100 hover:border-slate-200"
                          )}
                        >
                          <div className="flex items-center gap-4">
                            <div className={cn(
                              "w-12 h-12 rounded-xl flex items-center justify-center text-xl font-bold",
                              index === 0 && "bg-amber-100 shadow-sm",
                              index === 1 && "bg-slate-200",
                              index === 2 && "bg-orange-100",
                              index > 2 && "bg-slate-100"
                            )}>
                              {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`}
                            </div>
                            <div>
                              <p className="font-bold text-slate-800">{att.name}</p>
                              <p className="text-xs text-slate-400">{att.email}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-slate-900">{att.closed_today || 0}</p>
                            <p className="text-xs text-slate-400">conversas finalizadas</p>
                            {att.average_rating && (
                              <div className="flex items-center justify-end gap-1 mt-1">
                                <Star size={12} className="fill-amber-400 text-amber-400" />
                                <span className="text-xs font-semibold text-amber-600">{att.average_rating.toFixed(1)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SupervisorReports;
